import type { CSSProperties } from 'react';
import type { MotionElement, MotionShadow, MotionTextStyle, Paint } from '@/lib/motion';

export function paintToCss(paint?: Paint): string | undefined {
  if (!paint) return undefined;
  if (typeof paint === 'string') return paint;
  const stops = paint.stops.map((stop) => `${stop.color} ${stop.offset * 100}%`).join(', ');
  return paint.type === 'radial' ? `radial-gradient(circle, ${stops})` : `linear-gradient(${paint.angle ?? 0}deg, ${stops})`;
}

export function motionTransform(element: MotionElement): string {
  const transforms = [
    ...(element.perspective ? [`perspective(${element.perspective}px)`] : []),
    `translate(${-100 * (element.anchor?.x ?? 0)}%, ${-100 * (element.anchor?.y ?? 0)}%)`,
    `rotate(${element.rotation ?? 0}deg)`,
    `rotateX(${element.rotateX ?? 0}deg)`,
    `rotateY(${element.rotateY ?? 0}deg)`,
    `scale(${(element.scale ?? 1) * (element.scaleX ?? 1)}, ${(element.scale ?? 1) * (element.scaleY ?? 1)})`,
    `skew(${element.skewX ?? 0}deg, ${element.skewY ?? 0}deg)`,
  ];
  return transforms.join(' ');
}

export function motionBoxStyle(element: MotionElement): CSSProperties {
  const filters: string[] = [];
  if (element.blur) filters.push(`blur(${element.blur}px)`);
  if (element.brightness !== undefined) filters.push(`brightness(${element.brightness})`);
  if (element.saturate !== undefined) filters.push(`saturate(${element.saturate})`);
  if (element.shadow && element.type !== 'text' && element.type !== 'captions') {
    const shadow = element.shadow;
    filters.push(`drop-shadow(${shadow.x ?? 0}px ${shadow.y ?? 0}px ${shadow.blur ?? 0}px ${shadow.color ?? '#000000'})`);
  }
  const clip = typeof element.clip === 'object' ? element.clip : null;
  return {
    position: 'absolute', left: element.x ?? 0, top: element.y ?? 0,
    width: element.width, height: element.height,
    opacity: element.opacity ?? 1,
    transform: motionTransform(element),
    transformOrigin: `${(element.anchor?.x ?? 0) * 100}% ${(element.anchor?.y ?? 0) * 100}%`,
    transformStyle: 'preserve-3d',
    perspective: element.perspective,
    mixBlendMode: element.blendMode,
    filter: filters.length ? filters.join(' ') : undefined,
    overflow: element.clip === true ? 'hidden' : undefined,
    clipPath: clip && !clip.shape ? `inset(${clip.top ?? 0}px ${clip.right ?? 0}px ${clip.bottom ?? 0}px ${clip.left ?? 0}px)` : undefined,
  };
}

/** Text color, or a gradient painted through the glyphs. */
export function textFillCss(fill: Paint | undefined, fallback?: string): CSSProperties {
  if (typeof fill === 'object') {
    return { color: 'transparent', backgroundImage: paintToCss(fill), backgroundClip: 'text', WebkitBackgroundClip: 'text' };
  }
  const color = typeof fill === 'string' ? fill : fallback;
  return color ? { color } : {};
}

export const textStrokeCss = (stroke: string | undefined, width?: number): string | undefined => (
  stroke ? `${width ?? 1}px ${stroke}` : undefined
);

export const textShadowCss = (shadow: MotionShadow | undefined): string | undefined => (
  shadow ? `${shadow.x ?? 0}px ${shadow.y ?? 0}px ${shadow.blur ?? 0}px ${shadow.color ?? '#000000'}` : undefined
);

export function motionTextStyle(style: MotionTextStyle): CSSProperties {
  return {
    fontFamily: style.font ? `"${style.font}", ${/^(Georgia|Times New Roman|serif)$/i.test(style.font) ? 'serif' : 'sans-serif'}` : 'sans-serif',
    fontSize: style.size ?? 64, fontWeight: style.weight ?? 400,
    fontStyle: style.italic ? 'italic' : 'normal',
    ...textFillCss(style.fill, '#ffffff'),
    WebkitTextStroke: textStrokeCss(style.stroke, style.strokeWidth),
    paintOrder: 'stroke fill',
    letterSpacing: style.letterSpacing,
    lineHeight: style.lineHeight ?? 1.15,
    textAlign: style.align ?? 'left',
    textTransform: style.transform,
    textShadow: textShadowCss(style.shadow),
    whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
  };
}
